// TODO fix
/* eslint-disable @typescript-eslint/no-misused-promises */
import { Card, TERRA_GREEN, IGNIS_RED } from "@120wateraudit/waterworks";
import React, { useCallback, useMemo } from "react";
import { Button, Modal, Form } from "semantic-ui-react";
import styled from "styled-components";
import { ValidationErrors } from "final-form";
import { Form as FinalForm, Field } from "react-final-form";
import pluralize from "pluralize";

import { useInitialValues, Values } from "./hooks";
import DropdownBase from "src/components/Form/Dropdown";
import {
    getOptionsForCurrentStatus,
    isTerminalStatus,
} from "./StatusChangeDropdown";
import { toastError } from "src/utils/toast";
import { isApiError } from "src/utils/apiError";
import Submission, { SubmissionStatus } from "src/types/Submission";
import { PwsSubmissionPeriod, useUpdateSubmissionStatusesMutation } from "src/services/state-submission";
import Error from "src/components/Error";
import { titleCase } from "src/utils/format";

interface EnrichedFailedSubmission {
    id: number;
    systemName: string;
    error: string;
}

export const validate = (
    values: Values | Record<string, any>
): ValidationErrors => {
    const errors: ValidationErrors = {};

    if (!values.status) {
        errors.status = "Required";
    }

    return errors;
};

interface Props {
    closeModal: () => void;
    selected?: PwsSubmissionPeriod[] | Submission[];
    submissionId?: number;
}

const ChangeSubmissionStatusModal = ({
    closeModal,
    selected,
    submissionId,
}: Props): JSX.Element => {
    const required: (value: any) => undefined | "Required" = (value: any) =>
        value ? undefined : "Required";

    const submissionText = submissionId
        ? "this Submission"
        : pluralize("Submission", selected?.length, true);

    // Calculate status counts for initial display
    const statusCounts = useMemo(() => {
        if (!selected) return null;

        const counts: Record<string, number> = {};
        selected.forEach((s: any) => {
            const submission = (s.activeSubmission || s) as Submission;
            const status = submission.status;
            if (status) {
                counts[status] = (counts[status] || 0) + 1;
            }
        });
        return counts;
    }, [selected]);

    const [updateSubmissionStatuses, { data, error, isError, isLoading }] =
        useUpdateSubmissionStatusesMutation();
    const onSubmit = useCallback(
        async (values: Values) => {
            const { status } = values;
            const activeSubmissions: Submission[] | undefined = selected?.map((s) => {
                if (s.activeSubmission) {
                    return { ...s.activeSubmission } as unknown as Submission;
                }

                return { ...s } as unknown as Submission;
            });
            const submissionsToUpdate = submissionId
                ? [{ id: submissionId, status }]
                : activeSubmissions?.map((s) => ({ id: s.id, status }));

            try {
                submissionsToUpdate &&
                    (await updateSubmissionStatuses(submissionsToUpdate).unwrap());
            } catch {
                toastError("An error occurred, please try again.");
            }
        },
        [updateSubmissionStatuses, selected, submissionId]
    );

    const initialValues = useInitialValues();
    const statusOptions = getOptionsForCurrentStatus();
    const { data: responseData } = data ?? {};
    const { updatedSubmissions = [], submissionsNotUpdated = [] } = responseData ?? {};

    // Enrich submissions with system data from original selections
    const selectedMap = useMemo(() => {
        if (!selected) return new Map<number, Submission>();

        const map = new Map<number, Submission>();
        selected.forEach((s: any) => {
            const submission = (s.activeSubmission || s) as Submission;
            map.set(submission.id, submission);
        });
        return map;
    }, [selected]);

    const enrichedUpdatedSubmissions = useMemo(() => {
        if (!updatedSubmissions.length) return updatedSubmissions;

        return updatedSubmissions.map((submission) => ({
            ...submission,
            system: submission.system || selectedMap.get(submission.id)?.system,
        }));
    }, [updatedSubmissions, selectedMap]);

    const enrichedFailedSubmissions = useMemo(() => {
        if (!submissionsNotUpdated.length) return [];

        return submissionsNotUpdated.map((item): EnrichedFailedSubmission => {
            if (typeof item === 'string') {
                return {
                    id: 0,
                    systemName: item,
                    error: '',
                };
            }

            const systemName = selectedMap.get(item.id)?.system?.name ?? `Submission ${item.id}`;
            const errorMessage = item.error ?? item.message ?? '';

            return {
                id: item.id,
                systemName,
                error: errorMessage,
            };
        });
    }, [submissionsNotUpdated, selectedMap]);

    const submissionsUpdatedCount = enrichedUpdatedSubmissions.length;
    const updatedCountText = pluralize(
        "Submission",
        submissionsUpdatedCount,
        true
    );

    const errors = useMemo(() => {
        if (!error || !isError) {
            return undefined;
        }

        if (isApiError(error)) {
            return [error.data?.error];
        }

        return ["Please try again or contact support@120water.com"];
    }, [error, isError]);

    const selectedContainsApproved = selected?.some((s: any) => {
        const submission = (s.activeSubmission || s) as Submission;
        return submission.status === "approved";
    })

    return (
        <ChangeStatusContainer>
            <FinalForm
                initialValues={initialValues}
                onSubmit={onSubmit}
                render={({ handleSubmit, submitting, values }) => (
                    <Form
                        onSubmit={() => {
                            void handleSubmit();
                        }}
                    >
                        <>
                            <h3>
                                {`Change Submission ${submissionId ? "Status" : "Statuses"}`}
                            </h3>
                            <Modal.Content style={{ margin: "12px 0px" }}>
                                <Modal.Description>
                                    {!error && (
                                        <>
                                            <Field<string>
                                                wrapperStyling={{ margin: "16px 0px 12px 0px", display: "flex", gap: "12px", alignItems: "center" }}
                                                component={ModalDropdown}
                                                label="Status Choice"
                                                name="status"
                                                options={statusOptions}
                                                validate={required}
                                                disabled={!!data || submitting}
                                            />
                                            {!data && (
                                                <>
                                                    <P>
                                                        {`Are you sure you would like to change the status of
                          ${submissionText}?`}
                                                    </P>
                                                    {statusCounts && !submissionId && (
                                                        <StatusCountsContainer>
                                                            {Object.entries(statusCounts).map(
                                                                ([status, count]) => (
                                                                    <StatusCountItem key={status}>
                                                                        {SubmissionStatus[status as keyof typeof SubmissionStatus]}: {count}
                                                                    </StatusCountItem>
                                                                )
                                                            )}
                                                        </StatusCountsContainer>
                                                    )}
                                                    {values.status && isTerminalStatus(values.status) &&
                                                       <P>
                                                            Once you change the status to {titleCase(
                                                                SubmissionStatus[values.status]
                                                            )} the status cannot be changed again.
                                                        </P>
                                                    }
                                                    {selectedContainsApproved &&
                                                        <P>
                                                            Moving a submission out of Acceptance will exclude its data from the PTD, Insights Dashboard, and any following SDWIS Exports
                                                        </P>
                                                    }
                                                </>
                                            )}
                                        </>
                                    )}
                                    {responseData && (
                                      <>
                                        {enrichedUpdatedSubmissions?.length > 0 && (
                                          <SuccessCard>
                                            {`Successfully changed ${updatedCountText} Status`}
                                            <SystemList>
                                                            {enrichedUpdatedSubmissions?.map((submission) => (
                                                                <li key={submission.id}>
                                                                    {submission.system?.name || `Submission ${submission.id}`}
                                                                </li>
                                                            ))}
                                                        </SystemList>
                                                </SuccessCard>
                                             )}
                                            {enrichedFailedSubmissions &&
                                                enrichedFailedSubmissions.length > 0 && (
                                                    <FailureCard>
                                                        <FailureTitle>Failed to Update:</FailureTitle>
                                                        <SystemList>
                                                            {enrichedFailedSubmissions.map(
                                                                (item) => (
                                                                    <li key={`submission_not_updated_${item.id}`}>
                                                                        {item.systemName}{item.error && ` - ${item.error}`}
                                                                    </li>
                                                                )
                                                            )}
                                                        </SystemList>
                                                    </FailureCard>
                                                )}
                                        </>
                                    )}
                                    {errors && (
                                        <Error style={{ margin: "12px 24px" }} messages={errors} />
                                    )}
                                </Modal.Description>
                            </Modal.Content>
                            <Modal.Actions>
                                <Button style={{ marginLeft: 0 }} onClick={closeModal}>Cancel</Button>
                                <Button
                                    disabled={!!data || submitting || isLoading}
                                    loading={submitting || isLoading}
                                    primary
                                    type="submit"
                                >
                                    Confirm
                                </Button>
                            </Modal.Actions>
                        </>
                    </Form>
                )}
                validate={(values) => validate(values)}
            />
        </ChangeStatusContainer>
    );
};

const P = styled.p`
  font-size: 16px;
  margin: 8px 0;
`;

const ChangeStatusContainer = styled.div`
  margin: 0.5rem 2rem 2rem 2rem;
`;

const ModalDropdown = styled(DropdownBase)`
  &&& {
    width: fit-content;
    max-width: 300px;
  }
`;

const StatusCountsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;
  margin: 12px 0 16px 0;
`;

const StatusCountItem = styled.div`
  font-size: 14px;
  color: #333;
`;

const SystemList = styled.ul`
  margin: 8px 0 0 0;
  padding-left: 20px;
  
  li {
    font-size: 14px;
    margin: 4px 0;
  }
`;

const FailureTitle = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 14px;
`;

const SuccessCard = styled(Card)`
  background-color: ${TERRA_GREEN};
  margin-bottom: 10px;
`;

const FailureCard = styled(Card)`
  background-color: ${IGNIS_RED};
`;

export default ChangeSubmissionStatusModal;
